/* eslint-disable */
import React from 'react';
import { styled } from '@mui/material/styles';

import UIKitChipBase, { UIKitChipSize } from './ChipBase';
import UIKitIcon from '../Icon';
import { colors } from '../../theme/tokens.colors';
import { radius } from '../../theme/tokens.radius';

const IMPORTANT = ' !important';

export interface UIKitFilterChipProps {
  htmlId?: string;
  size?: UIKitChipSize;
  label: React.ReactNode;
  /** Icono a la izquierda (por ejemplo, el icono de filtro) */
  iconLeft?: React.ReactNode;
  /** Se muestra el botón "x" solo si se pasa esta prop */
  onClose?: () => void;
  /** aria-label para el botón de cierre */
  closeAriaLabel?: string;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

/** estilos del contenedor (el mismo <div> que renderiza ChipBase) - a
 * diferencia de un host tag (styled('div')), acá NO hay que filtrar
 * `disabled` con shouldForwardProp: UIKitChipBase es un componente propio
 * que necesita recibirlo como prop real (lo usa para su propio estilo
 * interno), no es un atributo HTML que pueda "filtrarse" al DOM. */
const StyledChipBase = styled(UIKitChipBase)(({ theme, disabled }) => ({
  borderColor: colors.neutralVariant[300] + IMPORTANT,
  backgroundColor: colors.specialColors.white + IMPORTANT,
  color: colors.neutral[900] + IMPORTANT,
  boxShadow: 'none',
  transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter,
  }),

  // hover "visual" (no es un botón, pero cambia el fondo)
  '&:hover': {
    backgroundColor: colors.neutralVariant[100] + IMPORTANT,
    borderColor: colors.neutralVariant[400] + IMPORTANT,
  },

  // pressed / dragged / focus-visible → todos iguales
  '&:active, &:focus-visible': {
    backgroundColor: colors.neutralVariant[200] + IMPORTANT,
    borderColor: colors.neutralVariant[500] + IMPORTANT,
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.18)',
  },

  ...(disabled && {
    backgroundColor: colors.neutralVariant[50] + IMPORTANT,
    borderColor: colors.neutralVariant[200] + IMPORTANT,
    color: colors.neutralVariant[500] + IMPORTANT,
    boxShadow: 'none',
    pointerEvents: 'none',
  }),
}));

/** botón circular que contiene la X */
const CloseButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'disabled',
})<{ size: UIKitChipSize; disabled?: boolean }>(({ size, disabled }) => ({
  border: 'none',
  padding: 0,
  margin: 0,
  marginLeft: size === 'small' ? 1 : 3,
  width: size === 'small' ? 14 : 16,
  height: size === 'small' ? 14 : 16,
  borderRadius: radius.full,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.neutralVariant[700],
  cursor: 'pointer',

  '&:hover': {
    boxShadow: '0px 0px 5px 1px' + colors.neutralVariant[300],
  },

  '&:active, &:focus-visible': {
    backgroundColor: '0px 0px 5px 1px' + colors.neutralVariant[500],
    outline: 'none',
  },

  // disabled: mismo prop que ya recibe StyledChipBase - antes se aplicaba
  // vía el truco de referencia compuesta de JSS ('& $closeButton' dentro de
  // la regla `disabled` del contenedor); acá alcanza con condicionar
  // directo sobre el propio prop, sin necesitar una referencia cruzada.
  ...(disabled && {
    opacity: 0.4,
    pointerEvents: 'none',
  }),
}));

const CloseIcon = styled(UIKitIcon)({
  fontSize: 14,
  lineHeight: 1,
  color: colors.specialColors.white,
  '&:hover': {
    color: colors.specialColors.bgVariant1,
  },
});

const UIKitFilterChip: React.FC<UIKitFilterChipProps> = ({
  htmlId,
  size = 'medium',
  label,
  iconLeft,
  onClose,
  closeAriaLabel = 'Remove filter',
  disabled = false,
  className,
  style,
}) => {
  // Si hay onClose, construimos el botón con la X
  const closeNode = onClose ? (
    <CloseButton
      type="button"
      size={size}
      disabled={disabled}
      aria-label={closeAriaLabel}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) {
          onClose();
        }
      }}
    >
      <CloseIcon name="close" size={size === 'small' ? 'xs' : 's'} />
    </CloseButton>
  ) : undefined;

  return (
    <StyledChipBase
      htmlId={htmlId}
      size={size}
      label={label}
      iconLeft={iconLeft}
      iconRight={closeNode}
      disabled={disabled}
      className={className}
      style={style}
    />
  );
};

export default UIKitFilterChip;
